import * as React from 'react';
import styled from 'styled-components';
import { rem } from 'polished';
import { __ } from '@bart.sk-eshop/react-i18n';
import OnClickOutsideComponent from '../OnClickOutsideComponent/OnClickOutsideComponent';
import { ChevronDownIcon } from '../../_layout/Header/HeaderNew/icons';

interface SortOption {
  name: string;
  sort: string;
  sortDir: string;
}

interface CategoryFilterBarProps {
  filterCount: number;
  onOpenFilter: () => void;
  sort: SortOption[];
  sortCurrent?: string;
  sortDirCurrent?: string;
  onSortChange: (sort: string, sortDir: string) => void;
}

function CategoryFilterBar({
  filterCount,
  onOpenFilter,
  sort,
  sortCurrent,
  sortDirCurrent,
  onSortChange,
}: CategoryFilterBarProps) {
  const [sortOpen, setSortOpen] = React.useState(false);

  const activeSort =
    sort.find((s) => s.sort === sortCurrent && s.sortDir === sortDirCurrent) ||
    sort[0];

  return (
    <Bar>
      <FilterButton type="button" onClick={onOpenFilter}>
        <SlidersIcon />
        <span>
          {__('Filtrovať')}
          {filterCount > 0 ? ` (${filterCount})` : ''}
        </span>
      </FilterButton>

      <SortSlot>
        <OnClickOutsideComponent onClickOutside={() => setSortOpen(false)}>
          <SortButton type="button" onClick={() => setSortOpen((o) => !o)}>
            <span>{activeSort ? activeSort.name : __('Zoradiť')}</span>
            <ChevronDownIcon className="chev" />
          </SortButton>
          {sortOpen && (
            <SortMenu>
              {sort.map((s, i) => {
                const active =
                  s.sort === sortCurrent && s.sortDir === sortDirCurrent;
                return (
                  <SortItem
                    key={i}
                    type="button"
                    className={active ? 'active' : ''}
                    onClick={() => {
                      onSortChange(s.sort, s.sortDir);
                      setSortOpen(false);
                    }}
                  >
                    {s.name}
                  </SortItem>
                );
              })}
            </SortMenu>
          )}
        </OnClickOutsideComponent>
      </SortSlot>
    </Bar>
  );
}

const SlidersIcon: React.FC = () => (
  <svg
    width="20"
    height="20"
    viewBox="0 0 20 20"
    fill="none"
    aria-hidden="true"
  >
    <path
      d="M18.332 5.41663H13.332"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeMiterlimit="10"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path
      d="M5.0013 5.41663H1.66797"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeMiterlimit="10"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path
      d="M8.33464 8.33333C9.94547 8.33333 11.2513 7.0275 11.2513 5.41667C11.2513 3.80584 9.94547 2.5 8.33464 2.5C6.7238 2.5 5.41797 3.80584 5.41797 5.41667C5.41797 7.0275 6.7238 8.33333 8.33464 8.33333Z"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeMiterlimit="10"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path
      d="M18.3333 14.5833H15"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeMiterlimit="10"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path
      d="M6.66797 14.5834H1.66797"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeMiterlimit="10"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path
      d="M11.6667 17.5C13.2775 17.5 14.5833 16.1942 14.5833 14.5834C14.5833 12.9725 13.2775 11.6667 11.6667 11.6667C10.0558 11.6667 8.75 12.9725 8.75 14.5834C8.75 16.1942 10.0558 17.5 11.6667 17.5Z"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeMiterlimit="10"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const Bar = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: ${rem(8)};
  margin: ${rem(4)} 0 ${rem(16)};
`;

const pill = `
  appearance: none;
  cursor: pointer;
  flex: 1 1 0;
  min-width: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: ${rem(6)};
  height: ${rem(46)};
  padding: 0 ${rem(12)};
  border-radius: ${rem(8)};
  background: #ffffff;
  border: ${rem(1)} solid #e0e0e0;
  box-shadow: 0 ${rem(2)} ${rem(8)} rgba(0, 0, 0, 0.05);
  color: #171717;
  font-family: inherit;
  font-size: ${rem(14)};
  font-weight: 600;
  line-height: 1;
`;

const FilterButton = styled.button`
  ${pill};
  gap: ${rem(10)};
  svg {
    width: ${rem(20)};
    height: ${rem(20)};
    flex: 0 0 auto;
  }
`;

const SortSlot = styled.div`
  position: relative;
  flex: 1 1 0;
  min-width: 0;
  display: flex;
  & > * {
    display: flex;
    flex: 1 1 0;
    min-width: 0;
  }
`;

const SortButton = styled.button`
  ${pill};
  width: 100%;
  span {
    flex: 0 1 auto;
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
  .chev {
    width: ${rem(16)};
    height: ${rem(16)};
    flex: 0 0 auto;
  }
`;

const SortMenu = styled.div`
  position: absolute;
  top: calc(100% + ${rem(4)});
  right: 0;
  left: 0;
  z-index: 30;
  background: ${({ theme }) => theme.color.white};
  border-radius: ${rem(8)};
  box-shadow: 0 ${rem(6)} ${rem(24)} rgba(0, 0, 0, 0.18);
  padding: ${rem(6)} 0;
  overflow: hidden;
`;

const SortItem = styled.button`
  appearance: none;
  border: none;
  background: none;
  cursor: pointer;
  width: 100%;
  text-align: left;
  font-family: inherit;
  font-size: ${rem(14)};
  font-weight: 600;
  line-height: 1.3;
  color: #171717;
  padding: ${rem(12)} ${rem(16)};
  &.active {
    font-weight: 700;
    background: #f5f5f5;
  }
`;

export default CategoryFilterBar;
